<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/store/auth'
import { setLocale, type SupportedLocale } from '@/i18n'
import arcoEnUS from '@arco-design/web-vue/es/locale/lang/en-us'
import arcoZhCN from '@arco-design/web-vue/es/locale/lang/zh-cn'
import SideMenu from '@/components/SideMenu.vue'
import BrandMark from '@/components/BrandMark.vue'
import { logoUrl, systemName } from '@/utils/branding'
import { isNarrow } from '@/utils/responsive'
import { prefs, settingsOpen, menuWidth, twoTone } from '@/utils/layout'
import NavSearch from '@/components/navbar/NavSearch.vue'
import NavNotifications from '@/components/navbar/NavNotifications.vue'
import PageSettings from '@/components/navbar/PageSettings.vue'
import NavIcon from '@/components/navbar/NavIcon.vue'
import NavAccount from '@/components/navbar/NavAccount.vue'
import { NAV_EXTRAS, NAV_LINKS, OVERLAYS } from '@/utils/navbar'

const route = useRoute()
const auth = useAuthStore()
const { t, locale } = useI18n()
const showShell = computed(() => route.meta.layout !== 'blank')

// 页面配置 (utils/layout.ts). A narrow screen keeps the navbar whatever
// the setting: its menu button is the only way into the menu there.
const showNavbar = computed(() => prefs.navbar || isNarrow.value)
const showSider = computed(() => prefs.menu && !isNarrow.value)
const collapsed = ref(false)
// Collapsed to the menu's 48px, the navbar's name cell has room for the
// logo alone; without one, the name's first letter stands in for it.
const brandInitial = computed(() => [...systemName.value][0]?.toUpperCase() ?? '')
// The widest the name may be in the cell: its padding off, and the logo
// (1.6em of 18px) and the 10px gap when there is one.
const nameMax = computed(() => Math.floor(menuWidth.value - 25 - (logoUrl.value ? 38.8 : 0)))

// Arco's own component-internal strings (Modal OK/Cancel, Table empty
// state, pagination, Popconfirm, ...) come from a separate locale system
// than vue-i18n and default to zh-CN regardless of the app's selected
// language unless explicitly fed through a-config-provider below.
const arcoLocale = computed(() => (locale.value === 'en-US' ? arcoEnUS : arcoZhCN))

// route.meta.title may be a plain string (legacy) or an i18n key like
// "nav.nodes" — anything containing a dot is looked up via $t.
const pageTitle = computed(() => {
  const raw = route.meta.title as string | undefined
  if (!raw) return route.name as string
  return raw.includes('.') ? t(raw) : raw
})

// Every tab names its own page: "节点列表 - <系统名称>". Follows the page,
// the language and the name as 系统设置 changes them.
watchEffect(() => {
  document.title = [pageTitle.value, systemName.value].filter(Boolean).join(' - ')
})

// The consoles speak Chinese and English and nothing else: one click
// switches to the other, its tooltip naming the one it switches to.
const otherLocale = computed<SupportedLocale>(() => (locale.value === 'zh-CN' ? 'en-US' : 'zh-CN'))
function switchLocale() {
  setLocale(otherLocale.value)
}

// Below Arco's lg breakpoint the side menu would take most of the screen,
// so — as Arco Design Pro does — it moves into a drawer the navbar opens.
// Picking a page closes it; so does widening the window past the point
// where the side bar comes back.
const drawerOpen = ref(false)
watch(() => route.fullPath, () => (drawerOpen.value = false))
watch(isNarrow, (narrow) => {
  if (!narrow) drawerOpen.value = false
})
</script>

<template>
  <a-config-provider :locale="arcoLocale">
    <router-view v-if="!showShell" />
    <!-- The frame is Arco Design Pro's, its navbar laid out as Tencent
         Cloud's and Aliyun's are — a search box beside the text links,
         bare icons, the account — and Arco's own light menu on the
         left: icons on the groups only, children indented under the group's
         text. The name sits centred over the menu; the menu collapses from
         its own foot (收起, as Tencent Cloud's does). -->
    <a-layout v-else class="layout">
      <a-layout-header v-if="showNavbar" class="navbar" :class="{ 'tone-dark': twoTone }">
        <div class="left-side" :class="{ framed: showSider }">
          <!-- On a narrow screen the name goes with the menu, into the
               drawer; the navbar keeps only the button that opens it — the
               same bare icon as the buttons on the right, so the bar reads
               as one row of controls. The plain menu icon: it opens the
               menu, where menu-fold's arrow said "collapse". -->
          <a-button
            v-if="isNarrow"
            class="nav-btn"
            type="text"
            shape="square"
            :aria-label="t('app.openMenu')"
            @click="drawerOpen = !drawerOpen"
          >
            <template #icon><icon-menu /></template>
          </a-button>
          <!-- Beside the side menu, the name sits centred in a cell exactly
               as wide as the menu: the menu and its name are one column,
               collapsing together from the 收起 row at the menu's foot. -->
          <template v-else-if="showSider">
            <div
              class="brand-cell"
              :class="{ collapsed }"
              :style="{ width: `${(collapsed ? 48 : menuWidth) + 1}px`, '--name-max': `${nameMax}px` }"
            >
              <span v-if="collapsed && !logoUrl" class="brand brand-initial">{{ brandInitial }}</span>
              <BrandMark v-else class="brand" :mini="collapsed" />
            </div>
          </template>
          <BrandMark v-else class="brand" />
        </div>
        <!-- The search box, at the right of what the name leaves, against the
             text links: centred, it stood a long way off from everything
             else. A narrow screen has no room: a button on the right opens it. -->
        <div v-if="!isNarrow" class="center">
          <NavSearch />
        </div>
        <!-- The clouds' row: a few text links — some with a panel of their
             own (utils/navbar.ts, NavMenu) — bare icons for the theme and
             页面配置 (where 皮肤 and 全屏 are), the language in words, the
             notifications, and past a rule the account (NavAccount). A phone
             keeps the icons that make sense on it. -->
        <ul class="right-side">
          <!-- Right of the search box, what this console adds there
               (utils/navbar.ts): the customer console's AI 助手. -->
          <li v-for="(x, i) in NAV_EXTRAS" :key="`extra-${i}`" class="extra-item">
            <component :is="x" />
          </li>
          <template v-if="!isNarrow">
            <li v-for="l in NAV_LINKS" :key="l.route" class="link-item">
              <component :is="l.panel" v-if="l.panel" />
              <router-link v-else :to="{ name: l.route }" class="nav-link">{{ t(l.label) }}</router-link>
            </li>
          </template>
          <li v-else>
            <NavSearch />
          </li>
          <!-- 自定义设置 everywhere: a phone picks its theme there too. -->
          <li>
            <a-tooltip :content="t('app.pageSettings')">
              <a-button class="nav-btn" type="text" shape="square" @click="settingsOpen = true">
                <template #icon><NavIcon name="settings" /></template>
              </a-button>
            </a-tooltip>
          </li>
          <!-- The language as Tencent Cloud's icon (中 and En swapping): a
               click is the other language. -->
          <li>
            <a-tooltip :content="t('app.switchLanguage')">
              <a-button class="nav-btn" type="text" shape="square" :aria-label="t('app.switchLanguage')" @click="switchLocale">
                <template #icon><NavIcon name="language" /></template>
              </a-button>
            </a-tooltip>
          </li>
          <li>
            <NavNotifications />
          </li>
          <li v-if="!isNarrow" class="rule-item">
            <a-divider direction="vertical" class="rule" />
          </li>
          <li class="account-item">
            <NavAccount />
          </li>
        </ul>
      </a-layout-header>
      <a-layout class="body">
        <a-layout-sider v-if="showSider" class="sider" :class="{ 'tone-dark': twoTone }" :width="menuWidth" :collapsed="collapsed" :collapsed-width="48">
          <SideMenu :collapsed="collapsed" collapsible @collapse="(v: boolean) => (collapsed = v)" />
        </a-layout-sider>
        <a-drawer
          v-else-if="isNarrow"
          :class="{ 'tone-dark': twoTone }"
          :visible="drawerOpen"
          placement="left"
          :width="250"
          :footer="false"
          :header="false"
          mask-closable
          :closable="false"
          :body-style="{ padding: 0 }"
          @cancel="drawerOpen = false"
        >
          <div class="drawer-brand">
            <BrandMark class="brand" />
          </div>
          <SideMenu :collapsed="false" :collapsible="false" />
        </a-drawer>
        <a-layout-content class="content">
          <!-- The page ground is grey, so a view's content needs a surface to
               sit on. Most views are a toolbar plus a table and have no card
               of their own; giving them one here keeps every page consistent.
               Views that paint their own panels opt out via meta.ownSurface,
               or they would end up double-carded. -->
          <!-- Keyed by path: a detail page read its id once, so going from
               one site to another — the global search does that — changed
               the address while the page went on showing the first. Only the
               path: moving between a page's sections is a query change and
               keeps the page. -->
          <router-view v-if="route.meta.bare" :key="route.path" />
          <template v-else>
            <!-- Views with a heading of their own (a site, the dashboard) set
                 meta.bare. -->
            <div v-if="route.meta.title && prefs.pageTitle" class="page-title">{{ t(route.meta.title as string) }}</div>
            <router-view v-if="route.meta.ownSurface" :key="route.path" />
            <a-card v-else :bordered="false" class="page-card">
              <router-view :key="route.path" />
            </a-card>
          </template>
        </a-layout-content>
      </a-layout>
    </a-layout>
    <template v-if="showShell">
      <PageSettings />
      <!-- With the navbar switched off, 页面配置 stays one click away. -->
      <a-button v-if="!showNavbar" class="floating-settings" type="primary" shape="circle" size="large"
                @click="settingsOpen = true">
        <template #icon><NavIcon name="settings" /></template>
      </a-button>
      <!-- What floats over this console's pages (utils/navbar.ts). -->
      <component :is="o" v-for="(o, i) in OVERLAYS" :key="i" />
    </template>
  </a-config-provider>
</template>

<style scoped>
/* Arco Design Pro's navbar — its height, ground and rule — laid out as
   Tencent Cloud's and Aliyun's: the name on the left, the rest on the
   right with the search box leading it. */
.layout {
  height: 100vh;
}
.navbar {
  display: flex;
  height: var(--navbar-h, 60px);
  flex: none;
  background-color: var(--color-bg-2);
  border-bottom: 1px solid var(--color-border);
  /* The gutter at either end; the account cell reaches through it. */
  --nav-edge: 20px;
}
.left-side {
  display: flex;
  flex: none;
  align-items: center;
  padding-left: 20px;
}
.left-side.framed {
  padding-left: 0;
}
.center {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  padding: 0 16px 0 24px;
}
.right-side {
  display: flex;
  flex: none;
  align-items: center;
  gap: 4px;
  margin: 0 0 0 auto;
  padding: 0 var(--nav-edge) 0 0;
  list-style: none;
}
/* The account (NavAccount) is a cell the bar's full height, out to the
   window's edge, as Tencent Cloud's is: its panel hangs from the bar's
   rule beneath it, flush with the window. */
.right-side .account-item {
  align-self: stretch;
  margin-right: calc(-1 * var(--nav-edge));
}
.right-side li {
  display: flex;
  align-items: center;
}
/* The words (.nav-link, base.css) are cells the bar's full height, the
   text links side by side, as Tencent Cloud's are. */
.right-side .link-item,
.right-side .extra-item {
  align-self: stretch;
}
/* Offered elsewhere, or off: no cell and no gap. */
.extra-item:empty {
  display: none;
}
.link-item + .link-item {
  margin-left: -4px;
}
/* The text links stand a little apart from the icons. Below 1200px they
   go, so the search box keeps its width: the menu has the same pages. */
@media (min-width: 1200px) {
  .link-item + li:not(.link-item) {
    margin-left: 8px;
  }
}
@media (max-width: 1199px) {
  .right-side .link-item {
    display: none;
  }
}
.rule {
  height: 20px;
  margin: 0 8px;
}
/* Arco Design Pro's navbar title as it is: a-typography-title at heading
   5, 18px — medium weight, the heading colour. */
.brand {
  max-width: 260px;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-text-1);
}
/* The side menu's column carried up into the navbar, without a rule of its
   own: the side bar's width plus its 1px rule (a-layout-sider is
   content-box, so the rule sits outside its width), the name centred over
   the menu — the right padding's extra pixel is where that rule falls —
   and collapsing in step with the side bar's transition. */
.brand-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 100%;
  padding: 0 13px 0 12px;
  overflow: hidden;
  transition:
    width 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    padding 0.2s cubic-bezier(0.34, 0.69, 0.1, 1);
}
.brand-cell .brand {
  max-width: 100%;
  transition: gap 0.2s cubic-bezier(0.34, 0.69, 0.1, 1);
}
/* At 48px, the logo alone, centred over the menu's icons. The name narrows
   away with the cell, so the logo slides to the middle instead of jumping
   there. */
.brand-cell.collapsed {
  padding: 0 1px 0 0;
}
.brand-cell.collapsed .brand {
  gap: 0;
}
.brand-cell :deep(.brand-name) {
  max-width: var(--name-max);
  transition:
    max-width 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    opacity 0.2s cubic-bezier(0.34, 0.69, 0.1, 1);
}
.brand-cell.collapsed :deep(.brand-name) {
  max-width: 0;
  opacity: 0;
}
.brand-initial {
  flex: none;
  width: 1.6em;
  text-align: center;
}
.floating-settings {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 100;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}
/* Above a settings page's save bar, floating (48px) or docked as a card
   (64px over the page's 20px margin), never on it; above the AI 助手's
   button. */
:global(body.has-save-bar) .floating-settings {
  bottom: 108px;
}
:global(body.has-assistant) .floating-settings {
  bottom: 88px;
}
:global(body.has-assistant-tall) .floating-settings {
  bottom: 176px;
}
/* The navbar's height and rule, so the drawer's top lines up with it. */
.drawer-brand {
  display: flex;
  align-items: center;
  height: var(--navbar-h, 60px);
  padding: 0 20px;
  border-bottom: 1px solid var(--color-border);
}
/* Flat, as the cards are: its rule against the page, not Arco's shadow. */
.sider {
  border-right: 1px solid var(--color-border);
  box-shadow: none;
}
.sider :deep(.arco-layout-sider-children) {
  overflow: hidden auto;
}
/* Below the navbar, the layout is exactly the rest of the screen and
   .content is the one thing that scrolls. Without min-height: 0 this flex
   item grew to its content's height, the whole document scrolled as well,
   and .content kept a scroll range of its own (the page title's height).
   A trackpad swipe locks onto the innermost scrollable element under the
   cursor, so the first swipe on every long page moved .content those few
   pixels and stopped dead; only the next one reached the document. */
.body {
  min-height: 0;
}
.content {
  overflow: auto;
  padding: 20px;
  /* The page ground is grey so white cards read as raised surfaces. */
  background: var(--color-fill-2);
  /* A column, so the card can take whatever height the title leaves. */
  display: flex;
  flex-direction: column;
}
/* Dark, the ground is the page's own black, the cards a step lighter
   (styles/dark.css): Arco's grey wash over it came out lighter than the
   cards, the other way round. */
body[arco-theme='dark'] .content {
  background: var(--color-bg-1);
}
/* Nothing in here may be squeezed to make room; .content scrolls instead. */
.content > * {
  flex-shrink: 0;
}
.page-title {
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 500;
  color: var(--color-text-1);
}
/* Fills the space under the title. It used to be min-height: 100%, which
   is 100% of .content on top of the title above it — so every page,
   however short, overflowed by the title's height and scrolled. */
.page-card {
  flex-grow: 1;
}
.page-card :deep(.arco-card-body) {
  padding: 20px 24px 24px;
}

/* Phones: tighter gutters everywhere, so the screen goes to content. */
@media (max-width: 767px) {
  .navbar {
    --nav-edge: 12px;
  }
  .left-side {
    padding-left: 12px;
  }
  .content {
    padding: 12px;
  }
  .page-title {
    margin-bottom: 12px;
    font-size: 18px;
  }
  .page-card :deep(.arco-card-body) {
    padding: 12px;
  }
}
</style>
